import React, { useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
    fetchOlderQueueCallRecords,
    fetchQueueCallRecords,
} from 'flow/actions/pbx-actions';
import { State } from 'flow/types/State';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { hasMoreQueueCallRecords } from 'flow/selectors/queue-selectors';
import { makeSelectQueueCallRecordKeysNotActiveDateGroups } from 'flow/selectors/call-records-selectors';
import { CALL_RECORD_INCLUDES_LIST } from 'flow/utils/call-record-fetch-includes';
import { QueueKey } from 'flow/types/EntityKeys';
import useAppContext from 'flow/hooks/useAppContext';
import CallRecordSkeleton, {
    CallRecordItemSkeleton,
} from 'flow/components/loaders/CallRecordSkeleton';
import EmptyState from 'flow/pages/messages/EmptyState';
import SectionTitle from '../SectionTitle';
import EndedQueueCallHistoryFetcher from './fetcher/EndedQueueCallHistoryFetcher';
import QueueHistoryList from './QueueHistoryList';
import {
    QUEUE_PAGE_SCROLLER_ID,
    forgetQueueScrollPosition,
    takeQueueScrollPosition,
} from './queue-scroll-position';

interface Props {
    queueKey: QueueKey;
}

const SectionQueueItemsHistory = ({ queueKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const prevQueueKey = useRef(queueKey);

    useEffect(() => {
        const scrollEl = document.getElementById(QUEUE_PAGE_SCROLLER_ID);
        if (prevQueueKey.current !== queueKey) {
            scrollEl?.scrollTo({ top: 0 });
            forgetQueueScrollPosition(prevQueueKey.current);
            prevQueueKey.current = queueKey;
        } else {
            // Back from a call's page: the list is where the user left it.
            const savedScroll = takeQueueScrollPosition(queueKey);
            if (savedScroll !== null && scrollEl) {
                requestAnimationFrame(() => {
                    scrollEl.scrollTo({ top: savedScroll });
                });
            }
        }
        void dispatch(
            fetchQueueCallRecords(
                queueKey,
                null,
                null,
                CALL_RECORD_INCLUDES_LIST,
            ),
        );
    }, [dispatch, queueKey]);

    const initialLoading = useAppSelector(
        (state) => !selectCallRecordsDataIsFetched(state, queueKey),
    );

    const selectDateGroups = useMemo(
        makeSelectQueueCallRecordKeysNotActiveDateGroups,
        [],
    );
    const callRecordKeysByDate = useAppSelector((state) =>
        selectDateGroups(state, queueKey),
    );

    const hasMore = useAppSelector((state) =>
        hasMoreQueueCallRecords(state, queueKey),
    );

    const recordCount =
        callRecordKeysByDate?.reduce(
            (sum: number, list: { size: number }) => sum + list.size,
            0,
        ) || 0;

    return (
        <>
            <EndedQueueCallHistoryFetcher queueKey={queueKey} />
            <SectionTitle className="mt-6">
                {i18next.t('call.history')}
            </SectionTitle>
            <InfiniteScroll
                className="overflow-visible!"
                next={() => {
                    setIsLoadingMore(true);
                    dispatch(fetchOlderQueueCallRecords(queueKey)).finally(() =>
                        setIsLoadingMore(false),
                    );
                }}
                dataLength={recordCount}
                hasMore={!!hasMore}
                loader={isLoadingMore ? <CallRecordItemSkeleton /> : null}
                scrollableTarget={QUEUE_PAGE_SCROLLER_ID}
                endMessage={
                    !initialLoading && recordCount > 0 ? (
                        <div className="flex justify-center py-9 text-sm text-gray-500">
                            {i18next.t('call.history.title.last')}
                        </div>
                    ) : null
                }
            >
                {initialLoading && <CallRecordSkeleton />}
                {!initialLoading && recordCount === 0 && (
                    <EmptyState
                        className="bg-transparent! antialiased"
                        message={i18next.t('call.history.related.empty')}
                    />
                )}
                {!initialLoading && recordCount > 0 && (
                    <QueueHistoryList
                        callRecordKeysByDate={callRecordKeysByDate}
                        queueKey={queueKey}
                    />
                )}
            </InfiniteScroll>
        </>
    );
};

export default SectionQueueItemsHistory;

const selectCallRecordsDataIsFetched = (state: State, queueKey: QueueKey) =>
    state.callRecordsData.getIn(['sortingData', 'queues', queueKey]) !==
    undefined;
